import { useIndexDB } from "@/contexts/IndexDB/useIndexDB";
import { PrepareRecordingProvider } from "@/contexts/PrepareRecording/PrepareRecordingProvider";
import { usePrepareRecording } from "@/contexts/PrepareRecording/usePrepareRecording";
import { RecordingStatusProvider } from "@/contexts/RecordingStatus/RecordingStatusProvider";
import { useRecordingStatus } from "@/contexts/RecordingStatus/useRecordingStatus";
import { persistRecordingLocalState } from "@/hooks/queries/useRecordingLocalState";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { RecordStatusEnum } from "@/pages/RecordsList/types";
import patchRecordingData from "@/services/patchRecordingData";
import { Button, ButtonVariant, Color, HorizontalFlex, IconSize, MediumTextL, MicOnIcon, ModalSize, Space, VerticalFlex } from "@enxcs/dap-ui-stratos";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { AgendaStep } from "../RecordingSteps/Agenda";
import { AttendeesStep } from "../RecordingSteps/Attendees";
import Stepper from "../Stepper";
import { BeforeRecordingContent } from "./BeforeRecording";
import { ModalStyled } from "./PrepareRecordingModal.styled";
import { ConfirmStep } from "./steps/Confirm";

enum PrepareRecordingSteps {
  AGENDA = "agenda",
  ATTENDEES = "attendees",
  CONFIRM = "confirm",
}

const PREPARE_RECORDING_STEPS_ORDER: PrepareRecordingSteps[] = [PrepareRecordingSteps.AGENDA, PrepareRecordingSteps.ATTENDEES, PrepareRecordingSteps.CONFIRM];

const PrepareRecordingModalContent = ({ onClose }: { onClose(): void }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState<PrepareRecordingSteps>(PrepareRecordingSteps.AGENDA);
  const [showBeforeRecordingContent, setShowBeforeRecordingContent] = useState(false);
  const agendasList = usePrepareRecording((state) => state.agendasList);
  const isLoadingAgendas = usePrepareRecording((state) => state.isLoadingAgendas);
  const selectedAgendaId = usePrepareRecording((state) => state.selectedAgendaId);
  const setSelectedAgendaId = usePrepareRecording((state) => state.setSelectedAgendaId);
  const attendeesList = usePrepareRecording((state) => state.attendeesList);
  const isLoadingAttendees = usePrepareRecording((state) => state.isLoadingAttendees);
  const selectedAttendees = usePrepareRecording((state) => state.selectedAttendees);
  const setSelectedAttendees = usePrepareRecording((state) => state.setSelectedAttendees);
  const toggleSelectedAttendeeId = usePrepareRecording((state) => state.toggleSelectedAttendeeId);
  const isAudioActive = usePrepareRecording((state) => state.isAudioActive);
  const selectedAttendeeIds = usePrepareRecording((state) => state.selectedAttendees.map((attendee) => attendee.id));
  const createNewRecordingHandler = usePrepareRecording((state) => state.createNewRecordingHandler);
  const canGoBeyondAgendaStep = !!selectedAgendaId;
  const canGoBeyondAttendeesStep = selectedAttendeeIds.length > 0;

  const { lang } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { errorHandler } = useErrorHandler();
  const { updateState } = useIndexDB();
  const { isRecording } = useRecordingStatus();
  const steps = [
    {
      id: PrepareRecordingSteps.AGENDA,
      title: t("prepareModal.agendaStep.title"),
      content: (
        <AgendaStep agendasList={agendasList} isLoadingAgendas={isLoadingAgendas} selectedAgendaId={selectedAgendaId} onSelectAgenda={setSelectedAgendaId} />
      ),
    },
    {
      id: PrepareRecordingSteps.ATTENDEES,
      title: t("prepareModal.guestsStep.title"),
      content: (
        <AttendeesStep
          attendeesList={attendeesList}
          isLoadingAttendees={isLoadingAttendees}
          selectedAttendees={selectedAttendees}
          onChangeSelectedAttendees={setSelectedAttendees}
          onToggleAttendee={toggleSelectedAttendeeId}
        />
      ),
    },
    {
      id: PrepareRecordingSteps.CONFIRM,
      title: t("prepareModal.completeStep.title"),
      content: <ConfirmStep />,
    },
  ];

  const activeStepIndex = PREPARE_RECORDING_STEPS_ORDER.indexOf(activeStep);
  const isFirstStep = activeStep === PrepareRecordingSteps.AGENDA;
  const isLastStep = activeStep === PrepareRecordingSteps.CONFIRM;
  const isNextDisabled =
    (activeStep === PrepareRecordingSteps.AGENDA && !canGoBeyondAgendaStep) || (activeStep === PrepareRecordingSteps.ATTENDEES && !canGoBeyondAttendeesStep);

  const handleOnClose = () => {
    if (isLoading) return;
    void updateState();
    onClose();
  };

  const handleStepChange = (nextStep: PrepareRecordingSteps) => {
    if (nextStep !== PrepareRecordingSteps.AGENDA && !canGoBeyondAgendaStep) {
      return;
    }

    if (nextStep === PrepareRecordingSteps.CONFIRM && !canGoBeyondAttendeesStep) {
      return;
    }

    setActiveStep(nextStep);
  };

  const handleStepIndexChange = (nextStepIndex: number) => {
    const nextStep = PREPARE_RECORDING_STEPS_ORDER[nextStepIndex];
    if (!nextStep) return;

    handleStepChange(nextStep);
  };

  const handleStartRecording = async () => {
    try {
      setIsLoading(true);
      const recordingData = await createNewRecordingHandler();
      if (!recordingData) {
        throw new Error("Recording data is undefined");
      }
      await persistRecordingLocalState(queryClient, recordingData);
      queryClient.invalidateQueries({ queryKey: ["recordingSubjects"] });
      navigate(`/${lang}/recording/${recordingData.recordingUUID}`);
      handleOnClose();
    } catch (error) {
      errorHandler(error, {
        showToast: true,
        eventName: "START-RECORDING",
        errorMessage: `Error when starting new recording: ${error}`,
      });
      return;
    } finally {
      setIsLoading(false);
    }
  };
  const handleSaveRecording = async () => {
    try {
      setIsLoading(true);
      const recordingData = await createNewRecordingHandler();
      if (!recordingData) {
        throw new Error("Recording data is undefined");
      }
      const { recordingUUID, duration } = recordingData;
      await patchRecordingData({ recordingId: recordingUUID, duration, status: RecordStatusEnum.READY_FOR_RECORDING });
      queryClient.invalidateQueries({ queryKey: ["recordingSubjects"] });
      handleOnClose();
    } catch (error) {
      errorHandler(error, {
        showToast: true,
        eventName: "SAVING-RECORDING",
        errorMessage: `Error when saving new recording: ${error}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalStyled
      size={ModalSize.S}
      isBeforeRecording={showBeforeRecordingContent}
      title={t("prepareModal.title")}
      closeTitle={t("buttons.close")}
      body={
        <VerticalFlex $gap={Space.V32} $padBottom={Space.V16}>
          {showBeforeRecordingContent ? (
            <BeforeRecordingContent />
          ) : (
            <>
              <MediumTextL color={Color.Neutral.V60}>{t("prepareModal.subtitle")}</MediumTextL>
              <Stepper steps={steps} activeStep={activeStepIndex} aria-label={t("prepareModal.title")} />
            </>
          )}
        </VerticalFlex>
      }
      onClose={handleOnClose}
      footer={
        <HorizontalFlex $justify={showBeforeRecordingContent ? "flex-end" : "space-between"} $gap={Space.V8}>
          {!showBeforeRecordingContent ? <Button variant={ButtonVariant.GHOST} label={t("buttons.cancel")} onClick={handleOnClose} /> : null}
          {showBeforeRecordingContent ? (
            <Button
              variant={ButtonVariant.PRIMARY}
              label={t("buttons.startRecording")}
              leftIcon={() => <MicOnIcon size={IconSize.V16} />}
              onClick={handleStartRecording}
              disabled={isLoading || !isAudioActive || isRecording}
            />
          ) : (
            <HorizontalFlex $gap={Space.V8}>
              {!isFirstStep && !isLastStep ? (
                <Button
                  variant={ButtonVariant.SECONDARY}
                  label={t("prepareModal.buttons.previousStep")}
                  onClick={() => handleStepIndexChange(activeStepIndex - 1)}
                />
              ) : null}
              {isLastStep ? (
                <Button variant={ButtonVariant.SECONDARY} label={t("prepareModal.buttons.save")} onClick={handleSaveRecording} disabled={isLoading} />
              ) : null}
              <Button
                variant={ButtonVariant.PRIMARY}
                label={isLastStep ? t("buttons.startRecording") : t("prepareModal.buttons.nextStep")}
                disabled={isNextDisabled || isLoading || (isRecording && isLastStep && !showBeforeRecordingContent)}
                leftIcon={isLastStep ? () => <MicOnIcon size={IconSize.V16} /> : undefined}
                onClick={() => {
                  if (isLastStep && !showBeforeRecordingContent) {
                    setShowBeforeRecordingContent(true);
                    return;
                  }

                  handleStepIndexChange(activeStepIndex + 1);
                }}
              />
            </HorizontalFlex>
          )}
        </HorizontalFlex>
      }
    />
  );
};

const PrepareRecordingModal = ({ onClose }: { onClose(): void }) => {
  return (
    <PrepareRecordingProvider>
      <RecordingStatusProvider>
        <PrepareRecordingModalContent onClose={onClose} />
      </RecordingStatusProvider>
    </PrepareRecordingProvider>
  );
};

export default PrepareRecordingModal;
